import { useState } from "react";
import styled from "styled-components";
import {
    AcceptIcon,
    Button,
    Flex,
    Loader,
    Text,
} from "@fluentui/react-northstar";
import { AppsListDetailFilled, MoneyHandFilled } from "@fluentui/react-icons";

import { useQuery } from "react-query";

import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    get_theme_levels_color,
    LEVELS_BACKEND,
} from "../../components/Badges/Status";
import { LEVELS } from "../../components/Badges/StatusFilled";
import { I18N_NS_EXPENSE, QUERY_KEY, TYPE_FORM } from "../../constants";
import { _api_get_detail_report_manager } from "../../api";
import { ContainerFluid } from "../../../../common/styles";
import EvolutionCard from "../ExpensesDetailReport/EvolutionCard";
import RefuseExpense from "./Dialogs/RefuseExpense";
import AcceptExpense from "./Dialogs/AcceptExpense";
import { get_symbol_currency } from "../../functions";
import AcceptAnotherAmountExpense from "./Dialogs/AcceptAnotherAmount";
import { ROUTES } from "../../../../constants/routes";
import StatusActionsDependRole, {
    STATUS_ROLE,
} from "../../components/Badges/StatusActionsDependRole";
import HeaderDetailExpenseReport from "../ExpensesDetailReport/HeaderDetailExpenseReport/HeaderDetailExpenseReport";
import EmbedAndCommentsCard from "../../components/EmbedAndCommentsCard/EmbedAndCommentsCard";
import { useMediaQuery } from "react-responsive";
import { DEVICE, DEVICE_MAX } from "../../../../constants/responsive";
import HeaderDetail, {
    SButtonRefuseNote,
    SSplitButton,
    WrapperButtonActions,
} from "../../components/Header/HeaderDetail";
import TableRowExpense from "../../components/Tables/RowExpense/TableRowExpense";
import { useToastGlobal } from "../../../../stores/useToastGlobal";
import { ROLES_CODE } from "../../../../constants/roles";

const ExpensesDetailReportAccountingManager = () => {
    const { t } = useTranslation(I18N_NS_EXPENSE);
    const navigate = useNavigate();
    const globalToast = useToastGlobal();
    const { id_report, id_user, role } = useParams();

    // states
    const [toggleComment, setToggleComment] = useState(false);
    const [comments, setComments] = useState([]); // used for toggle between comments card.
    const [embed, setEmbed] = useState(null);
    const [selectedExpense, setSelectedExpense] = useState(null);

    // dialogs
    const [openAcceptAnotherAmount, setOpenAcceptAnotherAmount] =
        useState(false);
    const [openAcceptCurrentAmount, setOpenAcceptCurrentAmount] =
        useState(false);
    const [openRefuseExpense, setOpenRefuseExpense] = useState(false);

    const isLessThenTablet = useMediaQuery({
        query: DEVICE_MAX.tablet,
    });

    const on_click_comment = (expense) => {
        const { histories, id } = expense;
        setComments(histories);
        setToggleComment(false);
        setSelectedExpense(id);
        return;
    };

    const on_click_view_document = (url_document) => {
        setToggleComment(false);
        return setEmbed(url_document);
    };

    const _goback = () => {
        return navigate(-1);
    };

    const {
        data: detail,
        isLoading,
        isFetching,
        refetch: refetch_detail_report,
    } = useQuery([QUERY_KEY + "::report_detail"], () =>
        _api_get_detail_report_manager({
            id_expense_report: id_report,
            id_user,
        })
    );

    const closeAllDialogs = () => {
        setOpenAcceptAnotherAmount(false);
        setOpenAcceptCurrentAmount(false);
        setOpenRefuseExpense(false);
    };

    const _onAcceptRefuseExpense = ([data, errors]) => {
        if (data?.success) {
            closeAllDialogs();

            globalToast.setShow({
                visible: true,
                content: errors,
                level: "success",
            });
            refetch_detail_report();
        } else {
            // show alert message here !
            globalToast.setShow({
                visible: true,
                content: errors,
                level: "danger",
            });
        }
    };

    if (isLoading) {
        return <Loader />;
    }

    const {
        name,
        created_date,
        reinvoiceable,
        client,
        expenses,
        history,
        status,
        amount_total,
        currency,
        amount_approved,
        user,
        object_code,
    } = detail?.expense_report;

    const { textColor } = get_theme_levels_color(LEVELS_BACKEND[status?.value]); // this is used for showing colors : red, green, yellow
    const currency_symbol = get_symbol_currency(currency);

    const isExpenseToValidate =
        LEVELS_BACKEND[status?.value] === LEVELS.recognised;

    const isCEOPage = role === ROLES_CODE.ceo.toLowerCase();

    return (
        <ContainerFluid styles={{ marginTop: "24px" }} column>
            {/* manager */}
            <HeaderDetail
                title={detail?.expense_report?.name}
                reinvoiceable={detail?.expense_report?.reinvoiceable}
                client_name={detail?.expense_report?.client?.name}
                objectCode={detail?.expense_report?.object_code?.name}
                disabledSendButton={true}
                showDotsMenu={false}
                showButtonSendNote={false}
                showSaveAuto={false}
                showButtonsAcceptReject={
                    isCEOPage ? false : isExpenseToValidate
                }
                onClickAcceptNote={() => {
                    return setOpenAcceptCurrentAmount(true);
                }}
                onClickRejectNote={() => {
                    return setOpenRefuseExpense(true);
                }}
                onClickAcceptAnotherAmount={() => {
                    return setOpenAcceptAnotherAmount(true);
                }}
                onClickGoBack={_goback}
            />
            <ContainerStyle gap="gap.large">
                {isFetching && <Loader size={"smallest"} />}
                <Wrapper
                    fill
                    column
                    gap="gap.small"
                    styles={{
                        height: "fit-content",
                        width: "100%",
                    }}
                >
                    <Flex vAlign="center" space="between" fill>
                        {/* header */}
                        <HeaderDetailExpenseReport
                            status={status}
                            user={user}
                            role={STATUS_ROLE.accounting_manager}
                        />
                        <StatusActionsDependRole
                            role={STATUS_ROLE.accounting_manager}
                            status={status?.value}
                        />
                        <Flex column hAlign="end">
                            <ButtonEvolution
                                content={history?.length}
                                icon={<AppsListDetailFilled />}
                                onClick={() => {
                                    setToggleComment((state) => !state);
                                    setComments([]);
                                    setEmbed(null);
                                    return;
                                }}
                                disabled={history?.length === 0}
                                iconPosition={"after"}
                                active={toggleComment}
                                text
                                primary
                                iconOnly
                            />
                        </Flex>
                    </Flex>
                    {/* white section */}
                    <TableRowExpense
                        IS_CAN_SEND={false}
                        detail={detail}
                        id_report={id_report}
                        REPORT_STATUS={detail?.expense_report?.status?.value}
                        isMobile={false}
                        selectedExpense={selectedExpense}
                        LENGTH_EXPENSES={expenses.length}
                        handle_click_comment={on_click_comment}
                        on_click_view_document={on_click_view_document}
                        disableMoreItem={
                            detail?.expense_report?.status?.value !== 3 ||
                            isCEOPage
                        }
                        onClickTitle={({
                            id_expense,
                            form_type,
                            id_report,
                        }) => {
                            // si c'est la page CEO, on le laisse pas accéder au formulaire de modification,
                            // il peut juste consulter les détails de la note de frais.
                            if (isCEOPage) return;

                            const expense_status =
                                detail?.expense_report?.status?.value;

                            if (expense_status === 3) {
                                // en cours de création
                                return navigate(
                                    ROUTES.expenses.update_form.custom_route(
                                        id_report,
                                        id_expense,
                                        TYPE_FORM[form_type]
                                    )
                                );
                            }
                        }}
                    />
                    {isExpenseToValidate && isLessThenTablet && (
                        <WrapperButtonActions vAlign="center" hAlign="center">
                            <SButtonRefuseNote
                                content={"Refuser la note"}
                                disabled={false}
                                secondary
                                flat
                                styles={{ color: "var(--color-danger)" }}
                                // loading={isSendingNote}
                                onClick={() => {
                                    return setOpenRefuseExpense(true);
                                }}
                            />
                            <div>
                                <SSplitButton
                                    menu={[
                                        {
                                            key: "accept_amount",
                                            content: "Accepter ce montant",
                                            icon: <AcceptIcon size="small" />,
                                            onClick: () => {
                                                return setOpenAcceptCurrentAmount(
                                                    true
                                                );
                                            },
                                            styles: {
                                                color: "var(--color-primary)",
                                            },
                                        },
                                        {
                                            key: "validate_another_amount",
                                            content:
                                                "Valider avec un autre montant",
                                            icon: <MoneyHandFilled />,
                                            onClick: () => {
                                                return setOpenAcceptAnotherAmount(
                                                    true
                                                );
                                            },
                                        },
                                    ]}
                                    button={{
                                        content: "Accepter la note",
                                        "aria-roledescription": "splitbutton",
                                        "aria-describedby":
                                            "instruction-message-primary-button",
                                        flat: true,
                                    }}
                                    toggleButton={{
                                        "aria-label": "plus d'options",
                                    }}
                                    onMainButtonClick={() => {
                                        return setOpenAcceptCurrentAmount(true);
                                    }}
                                    primary
                                    flat
                                    fluid
                                />
                            </div>
                        </WrapperButtonActions>
                    )}
                    <>
                        <RefuseExpense
                            id_report={id_report}
                            id_user={user?.id}
                            onAccept={_onAcceptRefuseExpense}
                            isAccountingManager={true}
                            open={openRefuseExpense}
                            onClose={() => {
                                return setOpenRefuseExpense(false);
                            }}
                        />
                        <AcceptExpense
                            id_report={id_report}
                            id_user={user?.id}
                            amount_number={amount_total}
                            amount={amount_total + " " + currency_symbol}
                            client={client}
                            reinvoiceable={reinvoiceable}
                            isAccountingManager={true}
                            onAccept={_onAcceptRefuseExpense}
                            open={openAcceptCurrentAmount}
                            onClose={() => {
                                return setOpenAcceptCurrentAmount(false);
                            }}
                        />
                        <AcceptAnotherAmountExpense
                            id_report={id_report}
                            id_user={user?.id}
                            amount={amount_total}
                            currency={currency_symbol}
                            client={client}
                            reinvoiceable={reinvoiceable}
                            isAccountingManager={true}
                            onAccept={_onAcceptRefuseExpense}
                            open={openAcceptAnotherAmount}
                            onClose={() => {
                                return setOpenAcceptAnotherAmount(false);
                            }}
                        />
                    </>
                </Wrapper>
                {/* right section */}
                <Flex>
                    {toggleComment && (
                        <Flex
                            fill
                            styles={{
                                marginTop: "8px",
                            }}
                        >
                            <EvolutionCard
                                history={history}
                                amount_approved={amount_approved}
                                currency={currency_symbol}
                                setToggleComment={setToggleComment}
                            />
                        </Flex>
                    )}
                </Flex>
                <ExpensesCommentCard fill>
                    {(comments.length > 0 || embed !== null) && (
                        <EmbedAndCommentsCard
                            comments={comments}
                            embed={embed}
                            onClose={() => {
                                setEmbed(null);
                                setComments([]);
                                setSelectedExpense(null);
                            }}
                        />
                    )}
                </ExpensesCommentCard>
            </ContainerStyle>
        </ContainerFluid>
    );
};

export default ExpensesDetailReportAccountingManager;
const ContainerStyle = styled(Flex)`
    max-width: 1440px;
    margin: 0 auto;
    margin-top: 24px;

    @media ${DEVICE.mobileS || DEVICE.mobileM || DEVICE.mobileL} {
        width: 100%;
        max-width: auto;
        flex-direction: column;
        padding: 0px;
        gap: 12px;
    }
    @media ${DEVICE.tablet} {
        flex-direction: column;
    }
    @media ${DEVICE.laptop} {
        flex-direction: row;
        gap: 6px;
        padding: 12px;
    }
    @media ${DEVICE.laptopL} {
        flex-direction: row;
        padding: 12px;
    }
`;
const ExpensesCommentCard = styled(Flex)`
    @media ${DEVICE.mobileS} {
        width: 300px;
        margin: 0 auto;
    }
    @media ${DEVICE.mobileM} {
        width: 355px;
        margin: 0 auto;
    }
    @media ${DEVICE.mobileL} {
        width: 405px;
        margin: 0 auto;
    }
    @media ${DEVICE.tablet} {
    }
    @media ${DEVICE.laptop} {
        margin: 0;
    }
    @media ${DEVICE.laptopL} {
        margin: 0;
    }
`;

const ButtonEvolution = styled(Button)`
    background-color: ${({ active }) => (!active ? "transparent" : "#EBEBEB")};
    padding-inline: 8px;
`;

const RefacturableClient = styled(Text)`
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;

    color: #616161;
`;

const RefacturableText = styled(Text)`
    font-weight: 400;
    font-size: 18px;
    line-height: 24px;
    color: #242424;
`;

const WhiteBoard = styled(Flex)`
    background-color: white;
    box-shadow: 0px 0.3px 0.9px rgba(0, 0, 0, 0.07),
        0px 1.6px 3.6px rgba(0, 0, 0, 0.11);
    /* padding: 16px 16px 32px 16px; */
    border-radius: 8px;
    padding-bottom: 16px;
    /* max-width: 600px;
    width: 100%; */
    @media ${DEVICE.mobileS} {
        width: 100%;
    }
    @media ${DEVICE.laptop} {
        width: 600px;
    }
    @media ${DEVICE.laptopL} {
        width: 800px;
    }
`;

const FlexFooter = styled(Flex)`
    padding-inline: 16px;
`;
const FlexBody = styled(Flex)``;
const FlexHeader = styled(Flex)``;

const CreatedAt = styled(Text)`
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;

    display: flex;
    align-items: center;

    color: #616161;
`;

const Wrapper = styled(Flex)`
    max-width: 850px;
    width: 100%;
`;

const Title = styled(Text)`
    font-style: normal;
    font-weight: 700;
    font-size: 18px;
    line-height: 24px;

    color: #242424;
`;
